import Backbone from 'backbone';
import vent from './site-search-events';
import {
    dateFromParamName,
    facetsParamName,
    offsetParamName,
    sortingParamName,
    dateToParamName,
    pagingParamName,
    pageSizeParamName,
    queryParamName,
    filterParamNamePrefix,
} from '../lib/params';
import { prefixParam } from '../lib/helpers';
import { FACETS_CHANGED } from './events';

const QueryModel = Backbone.Model.extend({
    defaults: {},
    initialize() {
        this.createQueryObj();

        // Listen to dom event
        document.addEventListener(FACETS_CHANGED, (e) =>
            this.updateQueryFacets(e.detail)
        );
    },

    createQueryObj() {
        const queryObj = {};
        const searchParams = new URLSearchParams(location.search);

        for (const [key, value] of searchParams.entries()) {
            queryObj[key] = value;
        }

        this.set(queryObj);

        return this.attributes;
    },
    /**
     * Retrieves the stored queryObj. Re-creates and returns
     */
    getQueryObj() {
        const queryObj = this.attributes;

        // Regenerate if not available
        return queryObj ? queryObj : this.createQueryObj();
    },

    updateQueryFacets(selectedFacets) {
        const queryObj = this.attributes;
        const searchParams = new URLSearchParams(location.search);

        if (selectedFacets) {
            queryObj[facetsParamName] = selectedFacets;
            searchParams.set(facetsParamName, selectedFacets);
        } else {
            delete queryObj[facetsParamName];
            searchParams.delete(facetsParamName);
        }

        // Remove any pagination when facets change
        delete queryObj[pagingParamName];
        searchParams.delete(pagingParamName);

        delete queryObj[offsetParamName];

        this.set(queryObj);
        this.updateHistory({ query: queryObj }, searchParams);

        // Trigger get data by passing signature of triggering component
        // Remark: facet functionality does not currently support the "sig",
        // which means only one facet per page in querystring will be
        // expected/supported
        vent.trigger('getResultsData', { signatures: [''] });
    },
    updateHistory(state, searchParams) {
        const params = searchParams?.toString
            ? '?' + searchParams.toString()
            : '';
        history.pushState(state, '', location.pathname + params);
    },
    /**
     * updates query text in the querystring
     */
    updateQueryText(queryText, signatures) {
        const queryObj = this.attributes;
        const searchParams = new URLSearchParams(location.search);

        if (signatures?.length == 0) signatures = [''];

        // Update queryObject for each signature
        for (const sig of signatures) {
            const prefix = prefixParam(sig);
            // Determining actual keys with signature if present.
            const queryKey = prefix + queryParamName;
            const offsetKey = prefix + offsetParamName;
            const pagingKey = prefix + pagingParamName;
            const pageSizeKey = prefix + pageSizeParamName;
            const sortingKey = prefix + sortingParamName;

            if (queryObj[queryKey] != queryText) {
                // Since search term has actually changed, clear out any facets that
                // may exists, and remove from the search params
                delete queryObj[facetsParamName];
                searchParams.delete(facetsParamName);

                // Since search term has actually changed, clear out any date filters
                // that may exists, and remove from the search params
                delete queryObj[dateFromParamName];
                searchParams.delete(dateFromParamName);

                delete queryObj[dateToParamName];
                searchParams.delete(dateToParamName);
            }
            queryObj[queryKey] = queryText;

            // Update if exists QueryText and delete other entries
            if (queryObj[queryKey]) {
                for (const [key] of Object.entries(queryObj)) {
                    if (key === queryKey) {
                        queryObj[key] = queryText;
                    } else if (
                        key === offsetKey ||
                        key === pagingKey ||
                        key === pageSizeKey ||
                        key === sortingKey ||
                        key.indexOf(filterParamNamePrefix) > -1
                    ) {
                        delete queryObj[key];
                    }
                }
            }
            this.set(queryObj);

            //Set new queryText in querystring
            searchParams.set(queryKey, queryText);

            //Remove keys to reset
            searchParams.delete(pagingKey);
            searchParams.delete(sortingKey);
        }

        this.updateHistory({ query: queryObj }, searchParams);
        //Trigger get data by passing signature of triggering component
        vent.trigger('getResultsData', { signatures });
    },
    updateQueryPagination(sig, offset, pageNumber) {
        const prefix = prefixParam(sig);
        const queryObj = this.getQueryObj();
        const searchParams = new URLSearchParams(location.search);

        //Update Object and save
        queryObj[prefix + offsetParamName] = offset;
        this.set(queryObj);

        // Update Querystring. Do not track offset in url. Only pg for page number
        // Check for signature and add if exists.
        searchParams.set(prefix + pagingParamName, pageNumber);

        this.updateHistory({ query: this.attributes }, searchParams);

        // Trigger get result data by passing signature of triggering component
        vent.trigger('getResultsData', { signatures: [sig] });
    },
    updateQuerySorting(sig, sortCode) {
        const prefix = prefixParam(sig);
        const queryObj = this.attributes;
        const searchParams = new URLSearchParams(location.search);
        const sortingKey = prefix + sortingParamName;

        // Update SortCode and save object
        queryObj[sortingKey] = sortCode;
        this.set(queryObj);

        // Set new sortCode in querystring
        searchParams.set(sortingKey, sortCode);

        this.updateHistory({ query: queryObj }, searchParams);

        // Trigger get data by passing signature of triggering component
        vent.trigger('getResultsData', { signatures: [sig] });
    },
    resetQueryPagination(sig, offset = 0, pageNumber = 1) {
        const prefix = prefixParam(sig);
        const queryObj = this.getQueryObj();
        const searchParams = new URLSearchParams(location.search);

        // Update Object and save
        queryObj[prefix + offsetParamName] = offset;
        this.set(queryObj);

        // Update Querystring. Do not track offset in url. Only pg for page number
        // Check for signature and add if exists.
        searchParams.set(prefix + pagingParamName, pageNumber);

        this.updateHistory({ query: this.attributes }, searchParams);
    },
    updateQueryDateRange(dateFrom, dateTo, signatures) {
        const queryObj = this.attributes;
        const searchParams = new URLSearchParams(location.search);

        if (signatures?.length === 0) signatures = [''];

        for (const sig of signatures) {
            const prefix = prefixParam(sig);
            const offsetKey = prefix + offsetParamName;
            const dateFromKey = prefix + dateFromParamName;
            const dateToKey = prefix + dateToParamName;
            const pagingKey = prefix + pagingParamName;
            const pageSizeKey = prefix + pageSizeParamName;

            // Delete any existing pagination keys from the query model object
            const pagingKeys = [offsetKey, pagingKey, pageSizeKey];
            for (const key of Object.keys(queryObj)) {
                if (pagingKeys.includes(key)) delete queryObj[key];
            }

            // Update dateFrom and save object
            queryObj[dateFromKey] = dateFrom;
            queryObj[dateToKey] = dateTo;

            // Set new dateFrom in querystring
            searchParams.set(dateFromKey, dateFrom);
            searchParams.set(dateToKey, dateTo);

            // Remove keys to reset
            searchParams.delete(pagingKey);
        }

        // Update QueryObj
        this.set(queryObj);

        this.updateHistory({ query: queryObj }, searchParams);

        // Trigger get data by passing signature of triggering component
        vent.trigger('getResultsData', { signatures });
    },
    removeQueryDateRange(signatures) {
        const queryObj = this.attributes;
        const searchParams = new URLSearchParams(location.search);

        if (signatures?.length === 0) signatures = [''];

        for (const sig of signatures) {
            const prefix = prefixParam(sig);

            const dateFromKey = prefix + dateFromParamName;
            const dateToKey = prefix + dateToParamName;

            // If present, delete dateFrom and dateTo from Query object
            delete queryObj?.[dateFromKey];
            delete queryObj?.[dateToKey];

            // Remove dateFrom and dateTo keys from URL
            searchParams.delete(dateFromKey);
            searchParams.delete(dateToKey);
        }

        //Update QueryObj
        this.set(queryObj);

        this.updateHistory({ query: queryObj }, searchParams);

        //Trigger get data by passing signature of triggering component
        vent.trigger('getResultsData', { signatures });
    },
    /**
     * Returns an array of filters set the queryObj
     */
    getQueryFilters(signature) {
        const queryObj = this.attributes;
        const prefix = prefixParam(signature);

        return Object.entries(queryObj)
            .filter(([key]) => key.startsWith(prefix + filterParamNamePrefix))
            .reduce((acc, [key, value]) => {
                return { ...acc, [key]: value };
            }, {});
    },
});

const queryModel = new QueryModel();

export default queryModel;
